<script>
import { onMount, onDestroy } from "svelte";
import { Stage, Layer, Image, Rect, Line } from "svelte-konva";
import throttle from "just-throttle";
import tcLib from "@cclib/lib/timecode.js";

import { viewportState, setScrollX } from "@app/lib/timeline/viewport.svelte.js";
import { peaksState } from "@app/lib/timeline/peaksState.svelte.js";
import { renderWaveformTile } from "@app/lib/timeline/waveformImage.js";
import { playerState } from "@app/store/playerStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { themeState } from "@app/store/themeStore.svelte.js";
import { getTimelinePalette } from "@app/lib/timeline/timelinePalette.js";

/**
 * Compact minimap rendered in its own Konva.Stage above the main timeline.
 *
 * Responsibilities:
 *   - Full-duration waveform (single offscreen canvas, refreshed when peaks
 *     change or width changes).
 *   - A "viewport box" indicating the time window currently visible in the
 *     main timeline.
 *   - A playhead line synced to `playerState.time`.
 *   - Click anywhere to seek; drag the viewport box to pan the main view.
 *   - HTML hover tooltip showing the timecode under the cursor (uses
 *     `tcLib.secToTc(t + incode, frameRate, dropFrame)` to match legacy).
 *
 * @typedef {Object} Props
 * @property {number} [height]
 */

/** @type {Props} */
let { height = 44 } = $props();

let palette = $derived(getTimelinePalette(themeState.effective));

let containerEl;
let resizeObserver;
let containerWidth = $state(0);

let hoverInfo = $state(null); // { x: number, label: string } | null
let dragInfo = null;          // { startMouseX, startScrollX }

let canvas = $state(null);

function measureContainer() {
    if (!containerEl) return;
    containerWidth = containerEl.clientWidth || 0;
}

const throttledMeasure = throttle(measureContainer, 16, { leading: false, trailing: true });

function regenerateCanvas() {
    const dur = playerState.duration || projectState?.media?.duration || 0;
    if (!containerWidth || !dur || !peaksState.data) {
        canvas = null;
        return;
    }
    const pxPerSec = containerWidth / dur;
    canvas = renderWaveformTile({
        peaks: peaksState.data.data,
        pxPerSec,
        durationSec: dur,
        startSec: 0,
        endSec: dur,
        height: height - 4,
        waveColor: palette.minimapWaveColor
    });
}

$effect(() => {
    void peaksState.signature;
    void playerState.duration;
    void containerWidth;
    void height;
    void themeState.effective;
    regenerateCanvas();
});

let duration = $derived(playerState.duration || projectState?.media?.duration || 0);

let viewportBox = $derived.by(() => {
    if (!duration || !containerWidth || !viewportState.pxPerSec) return null;
    const ratio = containerWidth / duration;
    const startSec = viewportState.scrollX / viewportState.pxPerSec;
    const widthSec = viewportState.containerWidth / viewportState.pxPerSec;
    return {
        x: startSec * ratio,
        width: Math.max(2, widthSec * ratio)
    };
});

let playheadX = $derived.by(() => {
    if (!duration || !containerWidth) return -1;
    return (playerState.time / duration) * containerWidth;
});

function timeFromPointerX(pointerX) {
    if (!duration || !containerWidth) return 0;
    return Math.max(0, Math.min(duration, (pointerX / containerWidth) * duration));
}

function formatTimecode(t) {
    const fr = projectState.frameRate || 29.97;
    const df = projectState.dropFrame ?? true;
    const incode = Number(projectState.incode) || 0;
    try {
        return tcLib.secToTc(t + incode, fr, df);
    } catch {
        return t.toFixed(2);
    }
}

function handleStagePointerMove(e) {
    const stage = e?.target?.getStage?.();
    const pointer = stage?.getPointerPosition?.();
    if (!pointer) return;
    hoverInfo = { x: pointer.x, label: formatTimecode(timeFromPointerX(pointer.x)) };
}

function handleStagePointerLeave() {
    hoverInfo = null;
}

function handleStageClick(e) {
    const stage = e?.target?.getStage?.();
    const pointer = stage?.getPointerPosition?.();
    if (!pointer) return;
    const newTime = timeFromPointerX(pointer.x);
    if (window.player) window.player.currentTime = newTime;
    // Also scroll the main timeline so the playhead is centered.
    if (viewportState.pxPerSec && viewportState.containerWidth) {
        setScrollX(newTime * viewportState.pxPerSec - viewportState.containerWidth / 2);
    }
}

function viewportBoxDragBound(pos) {
    if (!viewportBox) return pos;
    const minX = 0;
    const maxX = Math.max(0, containerWidth - viewportBox.width);
    return { x: Math.max(minX, Math.min(maxX, pos.x)), y: 0 };
}

function handleViewportBoxDragMove(e) {
    if (!duration || !containerWidth || !viewportState.pxPerSec) return;
    const node = e?.target;
    if (!node) return;
    const ratio = containerWidth / duration;
    const newStartSec = node.x() / ratio;
    setScrollX(newStartSec * viewportState.pxPerSec);
}

onMount(() => {
    measureContainer();
    if (typeof ResizeObserver !== "undefined" && containerEl) {
        resizeObserver = new ResizeObserver(() => throttledMeasure());
        resizeObserver.observe(containerEl);
    }
});

onDestroy(() => {
    resizeObserver?.disconnect();
});
</script>

<svelte:window onresize={throttledMeasure} />

<div
    bind:this={containerEl}
    class="minimap"
    style="height: {height}px;">
    {#if containerWidth > 0}
        <Stage
            width={containerWidth}
            height={height}
            divWrapperProps={{ class: "minimap-stage-wrapper" }}>
            <Layer
                onpointerclick={handleStageClick}
                onpointermove={handleStagePointerMove}
                onpointerleave={handleStagePointerLeave}>
                <Rect
                    x={0}
                    y={0}
                    width={containerWidth}
                    height={height}
                    fill={palette.minimapBg}
                    listening={true}
                    staticConfig={true} />

                {#if canvas}
                    <Image
                        x={0}
                        y={2}
                        width={containerWidth}
                        height={height - 4}
                        image={canvas}
                        listening={false} />
                {/if}

                {#if viewportBox}
                    <Rect
                        x={viewportBox.x}
                        y={0}
                        width={viewportBox.width}
                        height={height}
                        fill={palette.minimapViewportFill}
                        stroke={palette.minimapViewportStroke}
                        strokeWidth={1}
                        draggable={true}
                        dragBoundFunc={viewportBoxDragBound}
                        ondragmove={handleViewportBoxDragMove} />
                {/if}

                {#if playheadX >= 0}
                    <Line
                        points={[playheadX, 0, playheadX, height]}
                        stroke={palette.playhead}
                        strokeWidth={1}
                        listening={false} />
                {/if}
            </Layer>
        </Stage>
    {/if}

    {#if hoverInfo}
        <div
            class="minimap-tooltip"
            style="left: {Math.min(containerWidth - 80, Math.max(0, hoverInfo.x + 6))}px;">
            {hoverInfo.label}
        </div>
    {/if}
</div>

<style>
.minimap {
    background: var(--bs-body-bg);
    border-bottom: 1px solid var(--bs-border-color);
    overflow: hidden;
    position: relative;
    width: 100%;
}

.minimap :global(.minimap-stage-wrapper) {
    height: 100%;
    width: 100%;
}

.minimap-tooltip {
    background: var(--bs-body-bg);
    border: 1px solid var(--bs-border-color);
    border-radius: 3px;
    color: var(--bs-body-color);
    font-family: monospace;
    font-size: 11px;
    padding: 2px 6px;
    pointer-events: none;
    position: absolute;
    top: 4px;
    white-space: nowrap;
}
</style>
